import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { hexToRgba } from '~/styles/shadows'
import { handleVisitedFooter } from './HeaderClickEvents/Events'
import { useRouteLoaderData } from '@remix-run/react'
import type { BlockType } from '~/types/enums'
import DesktopHeader from './DesktopHeader'
import MobileHeader from './MobileHeader'
import { tv } from 'tailwind-variants'
import { CIOAppContext } from '~/types/common_cio'
import { TypeNavigationLink } from '~/types/contentful'
import { useGetUserInfo } from '~/contexts/MainAppContext'

export type HeaderProps = {
  bgColor?: string
  isDarkBg?: boolean
  isFlatHeader?: boolean
  headerScrollable?: boolean
  blockType?: BlockType
  eventsHeader?: boolean
}

const navbar = tv({
  base: 'backdrop-filter backdrop-blur backdrop-saturate-[200‰] will-change-[color] will-change-[box-shadow] will-change-[background-color] will-change-[border] max-md:pr-2 max-md:pl-4 fixed top-0 left-0 z-[4] w-full h-[var(--header-height)] py-0 px-8 text-neutral-100 transition-all duration-200 will-change-color box-shadow backdrop-filter',
  variants: {
    isDarkBg: {
      true: 'text-white',
      false: '',
    },
    isFlatHeader: {
      true: 'shadow-none',
    },
    isFixed: {
      true: 'text-neutral-100 bg-white/[0.92] shadow-template_low',
    },
    headerScrollable: {
      true: 'absolute shadow-none bg-blue-10 backdrop-filter-none',
    },
  },
  compoundVariants: [
    { isDarkBg: true, isFixed: true, class: 'text-neutral-100' },
    {
      isFlatHeader: true,
      isFixed: true,
      class: 'shadow-template_low',
    },
  ],
})

export const Header = ({
  isDarkBg,
  isFlatHeader,
  bgColor,
  headerScrollable,
  eventsHeader = false,
}: HeaderProps) => {
  const [isFixed, setFixed] = useState<boolean>(false)
  const [dropDownMenuActive, setDropDownMenuActive] = useState<boolean>(false)
  const { user, isLoading } = useGetUserInfo() || { isLoading: false }
  const userAuthorized = Boolean(user)

  const {
    data: { HeaderNav },
  } = useRouteLoaderData<CIOAppContext>('root') as CIOAppContext

  const [searchParams] = useSearchParams()
  const gclid = searchParams.get('gclid')

  const createAccountLink = gclid ? `/app/create-account?gclid=${gclid}` : '/app/create-account'

  const navItems = HeaderNav?.fields.headerMenuLink as TypeNavigationLink<
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >[]

  let bgColorVariant = hexToRgba('#fff', 0.92)
  if (bgColor) bgColorVariant = hexToRgba(bgColor, 0.92)
  let backgroundColor = isFlatHeader ? hexToRgba('#fff', 0.92) : bgColorVariant
  if (eventsHeader && bgColor) backgroundColor = bgColor

  if (userAuthorized && window?.location?.pathname === '/events') {
    window.location.href = '/app/my-events'
  }

  const handleScroll = useCallback(() => {
    const domHeight = document.documentElement.scrollHeight - window.innerHeight

    if (domHeight === window.scrollY) {
      handleVisitedFooter()
    }

    if (window.scrollY > 50) setFixed(true)
    else setFixed(false)
  }, [setFixed])

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return (
    <header className="relative h-[var(--header-height)] z-999">
      <nav
        className={navbar({
          isDarkBg,
          isFlatHeader,
          isFixed,
          headerScrollable,
        })}
        style={{
          backgroundColor: !isFixed && isDarkBg ? backgroundColor : undefined,
          zIndex: dropDownMenuActive ? 5 : 3,
        }}
      >
        <DesktopHeader
          setDropDownMenuActive={setDropDownMenuActive}
          navItems={navItems}
          userAuthorized={userAuthorized}
          isDarkBg={isDarkBg}
          isFixed={isFixed}
          createAccountLink={createAccountLink}
          isLoading={isLoading}
        />
        <MobileHeader
          navItems={navItems}
          userAuthorized={userAuthorized}
          isDarkBg={isDarkBg}
          isFixed={isFixed}
          createAccountLink={createAccountLink}
        />
      </nav>
    </header>
  )
}
